import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import Box from '@mui/material/Box';
import {
  Typography,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  Card,
  CardContent,
  Chip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  Edit as EditIcon,
  Payment as PaymentIcon,
  AttachMoney as MoneyIcon,
  LockOutlined as LockOutlinedIcon,
  Description as DescriptionIcon,
} from '@mui/icons-material';
import { DataGrid, GridToolbarContainer, GridToolbarExport } from '@mui/x-data-grid';
import { useMaterialUIController, useGlobalMDAlert, empresaProductosDesdeContexto } from 'context';
import { FP, PageHeader, StatCard, fpDataGridSx } from 'components/financepro';
import { tienePermisosModulo, getPermisosPorModulo } from 'lib/seguridad/permisosPorModulo';
import PrintListado from 'utils/PrintListado';

const PROGRAMA_LISTADO = 'listadoproductos';
const PROGRAMA_EDICION = 'gestionproductos';

const INPUT_SX = {
  '& .MuiOutlinedInput-root': {
    height: 46,
    minHeight: 46,
    borderRadius: 1.6,
    bgcolor: '#FFFFFF',
    '& fieldset': { borderColor: FP.color.border },
    '&:hover fieldset': { borderColor: FP.color.borderStrong },
    '&.Mui-focused fieldset': { borderColor: FP.color.navy },
  },
  '& .MuiInputLabel-root': { fontSize: '0.86rem' },
};

function normalizeTablaInventario(val) {
  if (val == null || val === '') return '';
  if (typeof val === 'string') return val;
  if (Array.isArray(val) && val.length > 0) {
    const first = val[0];
    if (first && typeof first === 'object') {
      const t =
        first.tabla_inventario ?? first.tabla ?? first.nombre ?? first.descripcion ?? first.INVENTARIO;
      if (t != null && t !== '') return String(t);
    }
  }
  return '';
}


function CustomToolbar() {
  return (
    <GridToolbarContainer sx={{ p: 1 }}>
      <GridToolbarExport
        sx={{
          color: FP.color.navy,
          textTransform: 'none',
          fontWeight: 700,
        }}
      />
    </GridToolbarContainer>
  );
}

const ListadoProductos = ({ onSeleccionarProducto }) => {
  const [controller] = useMaterialUIController();
  const {
    nombreNegocio,
    roleDetalles,
    usuarioLog,
    claveLog,
    direccion_empresa: direccionEmpresaCtx,
    rnc_empresa: rncEmpresaCtx,
  } = controller;
  const { showAlert } = useGlobalMDAlert();
  const showError = useCallback(
    (message) => showAlert({ message, color: 'error', autoHideDuration: 4000 }),
    [showAlert]
  );
  const servidor = process.env.REACT_APP_SERVIDOR || '';

  const tienePermisoListado = tienePermisosModulo(roleDetalles, PROGRAMA_LISTADO);
  const puedeEditar = tienePermisosModulo(roleDetalles, PROGRAMA_EDICION);
  const permisosprograma = getPermisosPorModulo(roleDetalles, PROGRAMA_LISTADO);


  const standalone = typeof onSeleccionarProducto !== 'function';

  const [filtroTipoProducto, setFiltroTipoProducto] = useState('');
  const [filtroCatProducto, setFiltroCatProducto] = useState('');
  const [filtroSerializado, setFiltroSerializado] = useState(null);
  const [filtroDescripcion, setFiltroDescripcion] = useState('');
  const [filtroDepartamento, setFiltroDepartamento] = useState('');
  const [filtroLocalidad, setFiltroLocalidad] = useState('');
  const [localidades, setLocalidades] = useState([]);
  const [departamentos, setDepartamentos] = useState([]);
  const [productosListado, setProductosListado] = useState([]);
  const [cargandoListado, setCargandoListado] = useState(false);
  const [tablaInventario, setTablaInventario] = useState('');
  const [datosEmpresa, setDatosEmpresa] = useState(null);
  const filtroDescripcionRef = useRef('');
  filtroDescripcionRef.current = filtroDescripcion;

  const idEmpresa = useMemo(() => empresaProductosDesdeContexto(nombreNegocio), [nombreNegocio]);

  /**
   * Datos de empresa para el encabezado de PrintListado.
   * Prioridad: configuración de factura; fallback a contexto de login / empresa.
   */
  const cargarDatosEmpresa = useCallback(async () => {
    if (!tienePermisoListado || !idEmpresa?.db_empresa) return;

    const nombreCtx =
      typeof nombreNegocio === 'string' && nombreNegocio.trim() && nombreNegocio !== 'false'
        ? nombreNegocio.trim()
        : '';
    const direccionCtx =
      typeof direccionEmpresaCtx === 'string' && direccionEmpresaCtx.trim()
        ? direccionEmpresaCtx.trim()
        : '';
    const rncCtx =
      typeof rncEmpresaCtx === 'string' && rncEmpresaCtx.trim() ? rncEmpresaCtx.trim() : '';
    const fallback = {
      nombre:
        idEmpresa?.name ||
        idEmpresa?.empresa ||
        idEmpresa?.nombreEmpresa ||
        nombreCtx ||
        'Empresa',
      direccion:
        idEmpresa?.direccion_empresa ||
        idEmpresa?.direccionEmpresa ||
        idEmpresa?.direccion ||
        direccionCtx,
      telefono:
        idEmpresa?.telefono ||
        idEmpresa?.telefonoEmpresa ||
        idEmpresa?.telefono_empresa ||
        '',
      rnc: idEmpresa?.rnc_empresa || idEmpresa?.rncEmpresa || idEmpresa?.rnc || rncCtx,
      email: idEmpresa?.email || idEmpresa?.emailEmpresa || '',
    };

    try {
      const response = await fetch(servidor + '/api/facturabackend_obtenerconfiguracion', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: idEmpresa,
          usuario: usuarioLog || '',
          password: claveLog || '',
          tipo_documento: 'Factura',
        }),
      });
      const config = response.ok
        ? (await response.json().catch(() => null))?.configuracion || null
        : null;

      setDatosEmpresa({
        nombre: (config?.nombreEmpresa || '').trim() || fallback.nombre,
        direccion: (config?.direccionEmpresa || '').trim() || fallback.direccion || '',
        telefono: (config?.telefonoEmpresa || '').trim() || fallback.telefono || '',
        rnc: (config?.rncEmpresa || '').trim() || fallback.rnc || '',
        email: (config?.emailEmpresa || '').trim() || fallback.email || '',
      });
    } catch {
      setDatosEmpresa(fallback);
    }
  }, [
    tienePermisoListado,
    idEmpresa,
    usuarioLog,
    claveLog,
    servidor,
    nombreNegocio,
    direccionEmpresaCtx,
    rncEmpresaCtx,
  ]);

  const cargarTablaInventario = useCallback(async () => {
    if (!idEmpresa) return;
    try {
      const servidor = process.env.REACT_APP_SERVIDOR || '';
      const response = await fetch(servidor + '/api/productosbackend_obtenertablainventario', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: idEmpresa,
          usuario: usuarioLog || '',
          password: claveLog || '',
          anomesfacturacion: "productosnoserializados",
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const raw = data.tabla_inventario;
        const normalized = normalizeTablaInventario(raw);
        setTablaInventario(data.tabla_inventario);
      } else {
        const errorData = await response.json().catch(() => ({}));
        showError(errorData.Error || 'Error al cargar tabla de inventario');
      }
    } catch {
      showError('Error de conexión al cargar tabla de inventario');
    }
  }, [idEmpresa, usuarioLog, claveLog, showError]);

  const cargarDepartamentos = useCallback(async () => {
    if (!idEmpresa) return;
    try {
      const servidor = process.env.REACT_APP_SERVIDOR || '';
      const response = await fetch(servidor + '/api/productosbackend_obtenerdepartamentos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: idEmpresa,
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setDepartamentos(data.departamentos || []);
      } else {
        const errorData = await response.json().catch(() => ({}));
        showError(errorData.Error || 'Error al cargar departamentos');
      }
    } catch {
      showError('Error de conexión al cargar departamentos');
    }
  }, [idEmpresa, usuarioLog, claveLog, showError]);

  const cargarLocalidades = useCallback(async () => {
    if (!idEmpresa) return;
    try {
      const servidor = process.env.REACT_APP_SERVIDOR || '';
      const response = await fetch(servidor + '/api/productosbackend_obtenerlocalidades', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: idEmpresa,
          usuario: usuarioLog || '',
          password: claveLog || '',
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setLocalidades(data.localidades || []);
      } else {
        const errorData = await response.json().catch(() => ({}));
        showError(errorData.Error || 'Error al cargar localidades');
      }
    } catch {
      showError('Error de conexión al cargar localidades');
    }
  }, [idEmpresa, usuarioLog, claveLog, showError]);

  const cargarListadoProductos = useCallback(async () => {
    const tabla = tablaInventario || leerAnomesFacturacionDesdeStorage();
    if (!idEmpresa || !tabla) {
      return;
    }

    setCargandoListado(true);
    try {
      const servidor = process.env.REACT_APP_SERVIDOR || '';
      const tablaNombre = typeof tabla === 'string' ? tabla : normalizeTablaInventario(tabla);
      const response = await fetch(servidor + '/api/productosbackend_listarproductos', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          id_empresa: idEmpresa,
          usuario: usuarioLog || '',
          password: claveLog || '',
          tabla_inventario: "productosnoserializados",
          tipoproducto: filtroTipoProducto || null,
          catproducto: filtroCatProducto || null,
          productoserializado: filtroSerializado,
          descripcion: filtroDescripcionRef.current || null,
          departamento: filtroDepartamento || null,
          localidad: filtroLocalidad || null,
        }),
      });

      if (response.ok) {
        const data = await response.json();
        setProductosListado(data.productos || []);
      } else {
        const errorData = await response.json().catch(() => ({}));
        showError(errorData.Error || 'Error al cargar listado');
      }
    } catch {
      showError('Error de conexión al cargar listado');
    } finally {
      setCargandoListado(false);
    }
  }, [
    idEmpresa,
    usuarioLog,
    claveLog,
    tablaInventario,
    filtroTipoProducto,
    filtroCatProducto,
    filtroSerializado,
    filtroDepartamento,
    filtroLocalidad,
    showError,
  ]);

  useEffect(() => {
    if (!tienePermisoListado || !idEmpresa) return;
    cargarTablaInventario();
    cargarDepartamentos();
    cargarLocalidades();
  }, [tienePermisoListado, idEmpresa, cargarTablaInventario, cargarDepartamentos, cargarLocalidades]);

  useEffect(() => {
    cargarDatosEmpresa();
  }, [cargarDatosEmpresa]);

  useEffect(() => {
    if (!tienePermisoListado || !tablaInventario) return;
    cargarListadoProductos();
  }, [
    tienePermisoListado,
    tablaInventario,
    filtroTipoProducto,
    filtroCatProducto,
    filtroSerializado,
    filtroDepartamento,
    filtroLocalidad,
    cargarListadoProductos,
  ]);

  const handleLimpiarFiltros = () => {
    setFiltroTipoProducto('');
    setFiltroCatProducto('');
    setFiltroSerializado(null);
    setFiltroDescripcion('');
    setFiltroDepartamento('');
    setFiltroLocalidad('');
  };

  const handleEditarProducto = useCallback(
    (codigo) => {
      if (onSeleccionarProducto) {
        onSeleccionarProducto(codigo);
      }
    },
    [onSeleccionarProducto]
  );

  const totalCosto = productosListado.reduce((acc, curr) => acc + (parseFloat(curr.costo) || 0), 0);
  const totalPrecio = productosListado.reduce((acc, curr) => acc + (parseFloat(curr.precio) || 0), 0);

  const formatearMonto = (value) =>
    value != null && value !== ''
      ? Number(parseFloat(value)).toLocaleString('es-DO', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : '0.00';

  /** Muestra localidad como en catálogo sucursal: "1-descripción"; si BD ya trae texto compuesto, se respeta. */
  const formatoLocalidadListado = useCallback(
    (raw) => {
      if (raw == null || raw === '') return '';
      const s = String(raw).trim();
      if (!s) return '';
      if (s.includes('-')) return s;
      const loc = localidades.find((l) => String(l.id) === s);
      return loc?.texto || s;
    },
    [localidades]
  );

  const subtitlePrint = useMemo(() => {
    const partes = [];
    if (filtroTipoProducto) partes.push(`Tipo: ${filtroTipoProducto}`);
    if (filtroCatProducto) partes.push(`Categoría: ${filtroCatProducto}`);
    if (filtroSerializado !== null) partes.push(`Serializado: ${filtroSerializado ? 'Sí' : 'No'}`);
    if (filtroDescripcion) partes.push(`Descripción: ${filtroDescripcion}`);
    if (filtroDepartamento) partes.push(`Depto.: ${filtroDepartamento}`);
    if (filtroLocalidad) {
      const loc = localidades.find((l) => String(l.id) === String(filtroLocalidad));
      partes.push(`Localidad: ${loc?.texto || filtroLocalidad}`);
    }
    return partes.length ? partes.join(' | ') : undefined;
  }, [
    filtroTipoProducto,
    filtroCatProducto,
    filtroSerializado,
    filtroDescripcion,
    filtroDepartamento,
    filtroLocalidad,
    localidades,
  ]);

  const columnasPrint = useMemo(
    () => [
      { id: 'codigo', label: 'Código' },
      {
        id: 'localidad',
        label: 'Localidad',
        render: (_, row) => formatoLocalidadListado(row?.localidad) || '—',
      },
      { id: 'descripcion', label: 'Descripción' },
      { id: 'tipoproducto', label: 'Tipo', render: (v) => v || 'N/A' },
      { id: 'catproducto', label: 'Categoría', render: (v) => v || '—' },
      {
        id: 'productoserializado',
        label: 'Serializado',
        align: 'center',
        render: (v) => (v === 'S' ? 'Sí' : 'No'),
      },
      { id: 'medida', label: 'Unidad', align: 'center', render: (v) => v || '—' },
      {
        id: 'costo',
        label: 'Costo',
        align: 'right',
        render: (v) => formatearMonto(v),
      },
      {
        id: 'mayor',
        label: 'Mayor',
        align: 'right',
        render: (v) => formatearMonto(v),
      },
      {
        id: 'precio',
        label: 'Precio',
        align: 'right',
        render: (v) => formatearMonto(v),
      },
      {
        id: 'existencia',
        label: 'Existencia',
        align: 'right',
        render: (v) => formatearMonto(v),
      },
    ],
    [formatoLocalidadListado]
  );

  const columns = useMemo(
    () => [
      { field: 'codigo', headerName: 'Código', flex: 0.9, minWidth: 110 },
      {
        field: 'localidad',
        headerName: 'Localidad',
        flex: 0.8,
        minWidth: 130,
        valueGetter: (_value, row) => formatoLocalidadListado(row?.localidad),
      },
      { field: 'descripcion', headerName: 'Descripción', flex: 1.4, minWidth: 180 },
      {
        field: 'tipoproducto',
        headerName: 'Tipo',
        flex: 0.65,
        minWidth: 100,
        renderCell: (params) => {
          const v = params.value || 'N/A';
          const isProd = v === 'Producto';
          return (
            <Chip
              label={v}
              size="small"
              sx={
                isProd
                  ? { bgcolor: FP.color.accentBlueSoft, color: FP.color.accentBlue, fontWeight: 600 }
                  : { bgcolor: FP.color.infoSoft, color: FP.color.info, fontWeight: 600 }
              }
            />
          );
        },
      },
      { field: 'catproducto', headerName: 'Categoría', flex: 0.85, minWidth: 130 },
      {
        field: 'productoserializado',
        headerName: 'Serializado',
        flex: 0.55,
        minWidth: 100,
        renderCell: (params) =>
          params.value === 'S' ? (
            <Chip label="Sí" size="small" color="success" />
          ) : (
            <Chip label="No" size="small" color="default" />
          ),
      },
      { field: 'medida', headerName: 'Unidad', flex: 0.45, minWidth: 90 },
      {
        field: 'costo',
        headerName: 'Costo',
        flex: 0.55,
        minWidth: 100,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value) =>
          value != null && value !== '' ? Number(parseFloat(value)).toFixed(2) : '0.00',
      },
      {
        field: 'mayor',
        headerName: 'Mayor',
        flex: 0.55,
        minWidth: 100,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value) =>
          value != null && value !== '' ? Number(parseFloat(value)).toFixed(2) : '0.00',
      },
      {
        field: 'precio',
        headerName: 'Precio',
        flex: 0.55,
        minWidth: 100,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value) =>
          value != null && value !== '' ? Number(parseFloat(value)).toFixed(2) : '0.00',
      },
      {
        field: 'existencia',
        headerName: 'Existencia',
        flex: 0.55,
        minWidth: 100,
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (value) =>
          value != null && value !== '' ? Number(parseFloat(value)).toFixed(2) : '0.00',
      },
      {
        field: 'accion',
        headerName: 'Acción',
        sortable: false,
        filterable: false,
        flex: 0.55,
        minWidth: 110,
        align: 'center',
        headerAlign: 'center',
        renderCell: (params) => (
          <Button
            size="small"
            startIcon={<EditIcon />}
            onClick={() => handleEditarProducto(params.row.codigo)}
            variant="outlined"
            disabled={permisosprograma.registro.editar === 'N' ? true : false || typeof onSeleccionarProducto !== 'function'}
            sx={{
              height: 34,
              px: 1.25,
              fontSize: '0.8rem',
              fontWeight: 600,
              borderColor: FP.color.border,
              color: FP.color.textPrimary,
              textTransform: 'none',
              '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
            }}
          >
            Editar
          </Button>
        ),
      },
    ],
    [permisosprograma.registro.editar, handleEditarProducto, formatoLocalidadListado]
  );

  if (!tienePermisoListado) {
    return (
      <Box
        component="main"
        sx={{
          minHeight: standalone ? '100vh' : 360,
          bgcolor: standalone ? FP.color.surface : 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: { xs: 2, sm: 3, md: 4 },
        }}
      >
        <Card
          elevation={0}
          sx={{
            maxWidth: 560,
            width: '100%',
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            textAlign: 'center',
            p: { xs: 3, md: 4 },
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: 1.6,
              mx: 'auto',
              mb: 2.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: FP.color.dangerSoft,
              color: FP.color.danger,
            }}
          >
            <LockOutlinedIcon sx={{ fontSize: 30 }} />
          </Box>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: '1.25rem',
              color: FP.color.textPrimary,
              mb: 1,
              letterSpacing: '-0.01em',
            }}
          >
            Acceso denegado
          </Typography>
          <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.95rem', mb: 1.2 }}>
            No tiene permisos para ver el listado de productos (programa "{PROGRAMA_LISTADO}" en roles).
          </Typography>
          <Typography sx={{ color: FP.color.textMuted, fontSize: '0.85rem' }}>
            Solicite al administrador el acceso correspondiente.
          </Typography>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      component="main"
      sx={{
        minHeight: standalone ? '100vh' : 'auto',
        bgcolor: standalone ? FP.color.surface : 'transparent',
      }}
    >
      <PageHeader
        title="Productos"
        subtitle="Listado de productos"
        cta={
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }} className="no-print">
            <PrintListado
              title={
                datosEmpresa?.nombre ||
                idEmpresa?.name ||
                idEmpresa?.empresa ||
                'Empresa'
              }
              empresa={datosEmpresa}
              reportTitle="Listado de productos"
              subtitle={subtitlePrint}
              columns={columnasPrint}
              data={productosListado}
              emptyMessage="No hay productos para imprimir"
              printButtonLabel="Imprimir"
              printButtonProps={{
                variant: 'outlined',
                sx: {
                  borderColor: FP.color.border,
                  color: FP.color.textPrimary,
                  bgcolor: '#FFFFFF',
                  borderRadius: 1.4,
                  px: 2,
                  height: 38,
                  textTransform: 'none',
                  fontWeight: 700,
                  boxShadow: 'none',
                  '&:hover': {
                    bgcolor: FP.color.surfaceAlt,
                    borderColor: FP.color.borderStrong,
                    boxShadow: 'none',
                  },
                },
              }}
              footer={`Total registros: ${productosListado.length} | Total costo: $${totalCosto.toLocaleString(
                'es-DO',
                { minimumFractionDigits: 2, maximumFractionDigits: 2 }
              )} | Total precio: $${totalPrecio.toLocaleString('es-DO', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}`}
            />
          </Box>
        }
      />

      <Box sx={{ p: { xs: 1.5, sm: 2, md: 3 } }}>
        <Card
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
              display: 'flex',
              alignItems: 'center',
              gap: 1.2,
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SearchIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Filtros de búsqueda
            </Typography>
            <Box sx={{ flex: 1 }} />
            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem', fontWeight: 600 }}>
              Tipo · Categoría · Serializado · Descripción · Depto. · Localidad
            </Typography>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth sx={INPUT_SX}>
                  <InputLabel id="lp-tipo">Tipo</InputLabel>
                  <Select
                    labelId="lp-tipo"
                    value={filtroTipoProducto}
                    onChange={(e) => setFiltroTipoProducto(e.target.value)}
                    label="Tipo"
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="Producto">Producto</MenuItem>
                    <MenuItem value="Servicio">Servicio</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth sx={INPUT_SX}>
                  <InputLabel id="lp-cat">Categoría</InputLabel>
                  <Select
                    labelId="lp-cat"
                    value={filtroCatProducto}
                    onChange={(e) => setFiltroCatProducto(e.target.value)}
                    label="Categoría"
                  >
                    <MenuItem value="">Todas</MenuItem>
                    <MenuItem value="Materia Prima">Materia Prima</MenuItem>
                    <MenuItem value="Producto Terminado">Producto Terminado</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth sx={INPUT_SX}>
                  <InputLabel id="lp-ser">Serializado</InputLabel>
                  <Select
                    labelId="lp-ser"
                    value={filtroSerializado === null ? '' : filtroSerializado ? 'S' : 'N'}
                    onChange={(e) => {
                      const valor = e.target.value;
                      setFiltroSerializado(valor === '' ? null : valor === 'S');
                    }}
                    label="Serializado"
                  >
                    <MenuItem value="">Todos</MenuItem>
                    <MenuItem value="S">Sí</MenuItem>
                    <MenuItem value="N">No</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth
                  label="Descripción"
                  value={filtroDescripcion}
                  onChange={(e) => setFiltroDescripcion(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={INPUT_SX}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') cargarListadoProductos();
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth sx={INPUT_SX}>
                  <InputLabel id="lp-dep">Departamento</InputLabel>
                  <Select
                    labelId="lp-dep"
                    value={filtroDepartamento}
                    onChange={(e) => setFiltroDepartamento(e.target.value)}
                    label="Departamento"
                  >
                    <MenuItem value="">Todos</MenuItem>
                    {departamentos.map((dept) => (
                      <MenuItem key={dept.id} value={dept.nombre || dept.texto}>
                        {dept.nombre || dept.texto}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth sx={INPUT_SX}>
                  <InputLabel id="lp-loc">Localidad</InputLabel>
                  <Select
                    labelId="lp-loc"
                    value={filtroLocalidad}
                    onChange={(e) => setFiltroLocalidad(e.target.value)}
                    label="Localidad"
                  >
                    <MenuItem value="">Todas</MenuItem>
                    {localidades.map((loc) => (
                      <MenuItem key={loc.id} value={String(loc.id)}>
                        {loc.texto || `${loc.id}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.2 }}>
                  <Button
                    variant="contained"
                    startIcon={<SearchIcon />}
                    onClick={cargarListadoProductos}
                    disabled={cargandoListado || !tablaInventario}
                    sx={{
                      height: 46,
                      bgcolor: FP.color.navy,
                      color: '#FFFFFF',
                      borderRadius: 1.4,
                      textTransform: 'none',
                      fontSize: '0.94rem',
                      fontWeight: 700,
                      boxShadow: 'none',
                      '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                      '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' },
                    }}
                  >
                    {cargandoListado ? 'Cargando…' : 'Buscar'}
                  </Button>
                  <Button
                    variant="outlined"
                    startIcon={<ClearIcon />}
                    onClick={handleLimpiarFiltros}
                    sx={{
                      height: 46,
                      borderColor: FP.color.border,
                      color: FP.color.textPrimary,
                      bgcolor: '#FFFFFF',
                      borderRadius: 1.6,
                      textTransform: 'none',
                      fontWeight: 700,
                      '&:hover': { bgcolor: FP.color.surfaceAlt, borderColor: FP.color.borderStrong },
                    }}
                  >
                    Limpiar filtros
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {productosListado.length > 0 && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                label="Total de registros"
                value={String(productosListado.length)}
                icon={DescriptionIcon}
                accent={FP.color.accentBlue}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                label="Total costo"
                value={`$${totalCosto.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                icon={PaymentIcon}
                accent={FP.color.successDark}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                label="Total precio"
                value={`$${totalPrecio.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                icon={MoneyIcon}
                accent={FP.color.navy}
              />
            </Grid>
          </Grid>
        )}

        <Card
          elevation={0}
          sx={{
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Resultados
            </Typography>
          </Box>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            {productosListado.length > 0 ? (
              <Box sx={{ width: '100%', overflowX: 'auto' }}>
                <DataGrid
                  rows={productosListado}
                  columns={columns}
                  loading={cargandoListado}
                  getRowId={(row) => (row.id != null ? String(row.id) : String(row.codigo))}
                  slots={{ toolbar: CustomToolbar }}
                  pageSizeOptions={[5, 10, 25, 50]}
                  initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                  disableRowSelectionOnClick
                  rowHeight={42}
                  sx={{ minWidth: { xs: 900, sm: '100%' }, height: 520, ...fpDataGridSx }}
                  autoHeight={false}
                />
              </Box>
            ) : (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                <DescriptionIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 2 }} />
                <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700 }} gutterBottom>
                  {cargandoListado ? 'Cargando…' : 'No hay datos para mostrar'}
                </Typography>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.9rem' }} textAlign="center" px={2}>
                  Ajuste los filtros y pulse «Buscar», o espere a que se cargue la tabla de inventario del período actual.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default ListadoProductos;
